<script lang="ts">
  import { page } from '$app/stores';

  import Seo from '$components/seo.svelte';
  import Accordion from '$components/accordion.svelte';
  import SplitLines from '$components/split-lines.svelte';
  import RadioButton from '$components/ui/radio-button.svelte';

  import { t } from '$lib/i18n';
  import { lang } from '$lib/lang';
  import { derived, get } from 'svelte/store';

  import type { FaqPage } from './content';
  import RichText from '$components/rich-text.svelte';
  import { cn } from '$lib/utils';
  import { region } from '$lib/region';

  export let data: FaqPage;

  const inputName = 'group';
  const groupNames = derived(lang, () => [
    get(t)('faq.default'),
    ...data.story.content.groups.map((grp) => grp.name)
  ]);

  let group = $page.url.searchParams.get(inputName) || $groupNames[0];

  $: if (!$groupNames.includes(group)) {
    group = $groupNames[0];
  }
</script>

<Seo
  title={data.story.content.seo_title}
  description={data.story.content.seo_description}
  image={data.story.content.seo_og_image}
/>

<div class="pb-24 pt-20 lg:py-30 px-4">
  <div class="lg:max-w-[600px] mx-auto">
    <section>
      <h1 class="text-4xl font-semibold">{data.story.content.title}</h1>
      <p class="text-md mt-5 text-primary/96">{data.story.content.description}</p>
    </section>

    <section class="mt-8">
      <form
        method="get"
        data-sveltekit-noscroll
        on:change={(e) => e.currentTarget.requestSubmit()}
        class="flex flex-wrap gap-2"
      >
        {#each $groupNames as name}
          <RadioButton value={name} name={inputName} label={name} id={name} bind:group />
        {/each}
      </form>
    </section>

    <section class="mt-8 flex flex-col gap-8">
      {#each data.story.content.groups.filter((grp) => grp.name === group || group === $t('faq.default')) as { name, items } (name)}
        <div class="flex flex-col gap-2">
          <h2 class="p-2 text-md text-primary/40">{name}</h2>
          {#each items as { question, answer, regions }}
            {#if (regions || []).includes($region) || !regions?.length}
              <Accordion as="h3" title={question} variant="product" group="faq">
                <!-- we have to test for "string" answer for legacy reasons-->
                {#if typeof answer === 'string'}
                  <SplitLines as="span" text={answer || ''} />
                {:else if answer}
                  <RichText
                    doc={answer}
                    class={cn(
                      '[&>p]:mb-8 [&>ul]:mb-8 [&>ol]:pb-8 inline [&>blockquote]:mb-8',
                      '[&>p>img]:rounded-sm',
                      '[&>p]:text-md-body [&>p]:text-primary/96',
                      '[&>p+*]:mt-6',
                      '[&>h2]:text-md [&>h2]:font-semibold [&>h2]:text-primary/96',
                      '[&>h2+*]:mt-1',
                      '[&>ul]:list-disc [&>ul]:list-inside [&>ul>li]:text-md-body [&>ul>li:not(:last-child)]:mb-6 [&>ul>li]:text-primary/96 [&>ul>li>p]:inline',
                      '[&>ol]:list-decimal [&>ol]:list-inside [&>ol>li]:text-md-body [&>ol>li:not(:last-child)]:mb-6 [&>ol>li]:text-primary/96 [&>ol>li>p]:inline [&>ol>li::marker]:text-accent'
                    )}
                  />
                {/if}
              </Accordion>
            {/if}
          {/each}
        </div>
      {/each}
    </section>
  </div>
</div>
